import { useHotel } from "@/hooks/useHotel";
import type { PartnerProviderType } from "@/types/partner-settings";
import {
  PartnerDestination,
  PartnerSettings,
  UpdatePartnerSettingsRequest,
  PartnerServicesFilters,
  PartnerServicesListResponse,
  PartnerServiceFilterOptions,
} from "@/types/partner-settings";
import { partnerSettingsService } from "@/services/partner-settings-service";
import { partnerRegionFlagKeys } from "@/components/pages/guesteria-partners/hooks/usePartnerRegionFlag";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { hotelPartnerCurationKeys } from "@/components/pages/guesteria-partners/hooks/hotel-partner-curation.hooks";
import { is404 } from "@/utils/httpErrors";

// `logs`/`syncStatus` key builders and `usePartnerSync`/`useGetPartnerLogs`
// removed (task 0036, contract 2026-09-29): the hotel-scoped sync/logs
// endpoints these fed now 404. See `adminPartnersService` for the
// Guesteria-admin-only replacements.
export const partnerSettingsKeys = {
  all: ["partnerSettings"] as const,

  settings: (hotelId: string) => [...partnerSettingsKeys.all, hotelId] as const,

  destinations: (hotelId: string, query: string, remote = false) =>
    [
      ...partnerSettingsKeys.all,
      "destinations",
      hotelId,
      query,
      remote ? "remote" : "cache",
    ] as const,

  // Task 0038 (owner, 2026-09-30): `period` is now a free-form cache-key
  // string (e.g. `${from}_${to}_${granularity}`) built by the caller from
  // the shared `usePartnerRevenuePeriod` range — no longer the old named
  // week/month/6mo/year enum.
  revenueAnalytics: (
    hotelId: string,
    period: string,
    provider: PartnerProviderType | string,
  ) =>
    [
      ...partnerSettingsKeys.all,
      "revenueAnalytics",
      hotelId,
      period,
      provider,
    ] as const,

  ordersSummary: (
    hotelId: string,
    period: string,
    provider: PartnerProviderType | string,
  ) =>
    [
      ...partnerSettingsKeys.all,
      "ordersSummary",
      hotelId,
      period,
      provider,
    ] as const,

  services: (hotelId: string) =>
    [...partnerSettingsKeys.all, "services", hotelId] as const,

  servicesList: (hotelId: string, filters?: PartnerServicesFilters) =>
    [...partnerSettingsKeys.services(hotelId), "list", filters] as const,

  servicesFilterOptions: (hotelId: string) =>
    [...partnerSettingsKeys.services(hotelId), "filterOptions"] as const,
};

export const useGetPartnerSettings = () => {
  const { hotelId } = useHotel();

  return useQuery<PartnerSettings | null>({
    queryKey: partnerSettingsKeys.settings(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await partnerSettingsService.getPartnerSettings(hotelId!);
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

/**
 * City selector search. By default it reads the backend city cache; with
 * `remote` it also asks the provider, and so runs only when `enabled` is set
 * (the "City search" button) and is never served from an old result.
 */
export const useSearchDestinations = (
  query: string,
  options: { remote?: boolean; enabled?: boolean } = {},
) => {
  const { hotelId } = useHotel();
  const remote = options.remote ?? false;

  return useQuery<PartnerDestination[]>({
    queryKey: partnerSettingsKeys.destinations(hotelId || "", query, remote),
    enabled: !!hotelId && query.length > 0 && (options.enabled ?? true),
    queryFn: () =>
      partnerSettingsService.searchDestinations(hotelId!, query, remote),
    staleTime: remote ? 0 : 5 * 60 * 1000,
    retry: remote ? false : 1,
    placeholderData: remote ? undefined : keepPreviousData,
  });
};

export const useUpdatePartnerSettings = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<PartnerSettings, unknown, UpdatePartnerSettingsRequest>({
    mutationFn: async (data) => {
      if (!hotelId) throw new Error("hotelId is required");
      return partnerSettingsService.updatePartnerSettings(hotelId, data);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.settings(hotelId),
      });
      qc.invalidateQueries({
        queryKey: partnerRegionFlagKeys.byHotel(hotelId),
      });
      // The destination (and so the catalog) may have changed.
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.services(hotelId),
      });
      qc.invalidateQueries({
        queryKey: hotelPartnerCurationKeys.curation(hotelId),
      });
      qc.invalidateQueries({
        queryKey: [...hotelPartnerCurationKeys.all, "catalog", hotelId],
      });
    },
  });
};

// Task 0028 — the single "Guesteria Partners" toggle for HOTEL_ADMIN /
// TENANT_ADMIN. Purpose-built PATCH endpoints, preferred over building a
// full `updatePartnerSettings({ partnersEnabled })` PUT payload. Contract
// 2026-09-28.
export const useEnablePartnerSettings = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<PartnerSettings, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return partnerSettingsService.enablePartnerSettings(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.settings(hotelId),
      });
      qc.invalidateQueries({
        queryKey: partnerRegionFlagKeys.byHotel(hotelId),
      });
      // The destination (and so the catalog) may have changed.
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.services(hotelId),
      });
      qc.invalidateQueries({
        queryKey: hotelPartnerCurationKeys.curation(hotelId),
      });
      qc.invalidateQueries({
        queryKey: [...hotelPartnerCurationKeys.all, "catalog", hotelId],
      });
    },
  });
};

export const useDisablePartnerSettings = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<PartnerSettings, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return partnerSettingsService.disablePartnerSettings(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.settings(hotelId),
      });
      qc.invalidateQueries({
        queryKey: partnerRegionFlagKeys.byHotel(hotelId),
      });
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.services(hotelId),
      });
    },
  });
};

export const useGetPartnerServices = (filters?: PartnerServicesFilters) => {
  const { hotelId } = useHotel();

  return useQuery<PartnerServicesListResponse>({
    queryKey: partnerSettingsKeys.servicesList(hotelId || "", filters),
    enabled: !!hotelId,
    queryFn: () => partnerSettingsService.getPartnerServices(hotelId!, filters),
    placeholderData: keepPreviousData,
    // The catalog changes in the background (a sync run by a Guesteria admin),
    // so each visit asks again; the cached list shows only until the answer.
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useGetPartnerServicesFilterOptions = () => {
  const { hotelId } = useHotel();

  return useQuery<PartnerServiceFilterOptions>({
    queryKey: partnerSettingsKeys.servicesFilterOptions(hotelId || ""),
    enabled: !!hotelId,
    queryFn: () =>
      partnerSettingsService.getPartnerServicesFilterOptions(hotelId!),
    staleTime: 0,
    refetchOnMount: "always",
  });
};
